import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { CheckCircle, XCircle, Clock } from "@phosphor-icons/react";

export default function Payments() {
  const [packages, setPackages] = useState([]);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(null);

  const load = async () => {
    try {
      const [p, t] = await Promise.all([
        api.get("/payments/packages"),
        api.get("/payments/transactions"),
      ]);
      setPackages(p.data.packages);
      setTxns(t.data.transactions);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  useEffect(() => { load(); }, []);

  const checkout = async (id) => {
    setLoading(id);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: id,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setLoading(null);
    }
  };

  const statusIcon = (s) => {
    if (s === "paid") return <CheckCircle size={14} weight="fill" className="text-[#10B981] inline-block mr-1" />;
    if (s === "failed" || s === "expired") return <XCircle size={14} weight="fill" className="text-[#EF4444] inline-block mr-1" />;
    return <Clock size={14} weight="fill" className="text-[#F59E0B] inline-block mr-1" />;
  };

  return (
    <div className="p-8">
      <header className="mb-6 pb-6 border-b border-[#262626]">
        <div className="text-[10px] uppercase tracking-widest text-[#71717A] mb-2">Billing</div>
        <h1 className="font-display text-4xl font-black tracking-tight">Payments</h1>
        <p className="text-sm text-[#A1A1AA] mt-2">
          Stripe test mode. Card <code className="font-mono text-white bg-[#141414] px-1.5 py-0.5">4242 4242 4242 4242</code>, any future expiry & CVC.
        </p>
      </header>

      <section className="mb-10">
        <h2 className="text-[10px] uppercase tracking-widest text-[#71717A] mb-4">Packages</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {packages.map((p) => (
            <Card key={p.id} className="rounded-sm bg-[#141414] border-[#262626] p-6 flex flex-col" data-testid={`package-${p.id}`}>
              <div className="text-[10px] uppercase tracking-widest text-[#71717A] mb-2">{p.id}</div>
              <div className="font-display text-2xl font-bold mb-1">{p.name}</div>
              <div className="font-display text-4xl font-black tracking-tight my-4">
                ${p.amount.toFixed(2)}
                <span className="text-sm text-[#71717A] font-normal ml-1">/ one-time</span>
              </div>
              <Button
                onClick={() => checkout(p.id)}
                disabled={loading === p.id}
                className="mt-auto rounded-sm bg-[#0055FF] hover:bg-[#0044CC]"
                data-testid={`checkout-${p.id}`}
              >
                {loading === p.id ? "Redirecting..." : "Buy now"}
              </Button>
            </Card>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-[10px] uppercase tracking-widest text-[#71717A] mb-4">Transactions</h2>
        <div className="border border-[#262626] bg-[#0F0F0F]" data-testid="transactions-table">
          <Table>
            <TableHeader>
              <TableRow className="border-[#262626] hover:bg-transparent">
                <TableHead className="text-[#71717A] uppercase text-[10px] tracking-widest">Session</TableHead>
                <TableHead className="text-[#71717A] uppercase text-[10px] tracking-widest">Package</TableHead>
                <TableHead className="text-[#71717A] uppercase text-[10px] tracking-widest">Amount</TableHead>
                <TableHead className="text-[#71717A] uppercase text-[10px] tracking-widest">Status</TableHead>
                <TableHead className="text-[#71717A] uppercase text-[10px] tracking-widest">Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {txns.length === 0 && (
                <TableRow className="border-[#262626]"><TableCell colSpan={5} className="text-center py-12 text-[#71717A] dashed">No transactions yet.</TableCell></TableRow>
              )}
              {txns.map((t) => (
                <TableRow key={t.id} className="border-[#262626] hover:bg-[#141414]">
                  <TableCell className="font-mono text-xs text-[#A1A1AA]">{t.session_id?.slice(0, 20)}...</TableCell>
                  <TableCell>{t.package_id}</TableCell>
                  <TableCell className="font-mono">${(t.amount || 0).toFixed(2)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="rounded-sm border-[#262626] font-mono text-[10px] uppercase">
                      {statusIcon(t.payment_status)} {t.payment_status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-[#A1A1AA] text-xs font-mono">
                    {t.created_at ? new Date(t.created_at).toLocaleString() : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </section>
    </div>
  );
}
